<script setup lang="ts">
import type { Ref } from "vue";
import { ref, watch, computed } from "vue";
import { ReservationPreferences, SeatDirection, SeatPosition, CoachType, ReservationRequestModel } from "@/models/basket/ReservationPreferences";
import { handleError } from "@/services/AlertService";
import { useField } from "vee-validate";
import { useRoute, useRouter } from "vue-router";
import { useBookingData } from "@/stores/booking-storage";
import { addTrip } from "@/graphql/functions/addTrip";

const bookingStore = useBookingData();
const isLoading: Ref<boolean> = ref(false);
const reservationTypeField = useField<boolean>("hasOptedForReservation");
const hasOptedForReservation = reservationTypeField.value;
const tableSeatSelected: Ref<boolean> = ref(false);
const nearLuggageRack: Ref<boolean> = ref(false);
const powerSocket: Ref<boolean> = ref(false);
const nearToilet: Ref<boolean> = ref(false);
const selectedSeatDirection: Ref<any> = ref(SeatDirection.NoPreference);
const selectedPosition: Ref<any> = ref(SeatPosition.NoPreference);
const selectedCoachType: Ref<any> = ref(CoachType.NoPreference);
const toggleModel: Ref<any> = ref("");
const route = useRoute();
const seatReservationRequired: boolean = route.query.hasAnyMandatoryReservations === "true";
const router = useRouter();
const features: Ref<Array<string>> = ref([]);
const seatPositionsDropdown = computed(() => [
    { title: "Any", value: SeatPosition.NoPreference },
    { title: "Aisle", value: SeatPosition.Aisle },
    { title: "Individual", value: SeatPosition.Individual },
    { title: "Middle", value: SeatPosition.Middle },
    { title: "Window", value: SeatPosition.Window },
]);

const coachType = computed(() => [
    { title: "Any", value: CoachType.NoPreference },
    { title: "Quiet", value: CoachType.Quiet },
]);

function toggleFeature(feature: string, isEnabled: Ref<boolean>) {
    let isEnabledValue = isEnabled.value;
    isEnabledValue = !isEnabledValue;
    if (isEnabledValue) {
        features.value.push(feature);
    } else {
        features.value = features.value.filter((e) => e !== feature);
    }
}

function toggleTableSeat() {
    toggleFeature(ReservationRequestModel.TableSeat, tableSeatSelected);
}
function toggleLuggageRack() {
    toggleFeature(ReservationRequestModel.NearLuggage, nearLuggageRack);
}
function togglePowerSocket() {
    toggleFeature(ReservationRequestModel.PowerSocket, powerSocket);
}
function toggleNearToilet() {
    toggleFeature(ReservationRequestModel.NearToilet, nearToilet);
}

// Adds the Trip to the basket. If the user has selected any reservation options these are passed in the call. If a booking ID already exists this Trip will be added to an existing order, otherwise a new one will be created.
async function createBooking() {
    const fareKeys = route.query.fareKeys as string[];
    const outKey = route.query.outKey as string;
    const inKey = route.query.inKey as string;
    const searchId = route.query.searchId as string;
    let bookingDetails: any;
    const bookingId = bookingStore.checkExistingBooking();

    try {
        isLoading.value = true;
        if (seatReservationRequired || hasOptedForReservation.value) {
            const position = selectedPosition.value === SeatPosition.NoPreference ? null : selectedPosition.value;
            const coachTypeSelection = selectedCoachType.value === CoachType.NoPreference ? null : selectedCoachType.value;
            const direction = selectedSeatDirection.value === SeatDirection.NoPreference ? null : selectedSeatDirection.value;

            bookingDetails = await addTrip(coachTypeSelection, features.value, direction, position, true, bookingId, fareKeys, outKey, inKey, searchId);
        } else {
            bookingDetails = await addTrip(null, null, null, null, false, bookingId, fareKeys, outKey, inKey, searchId);
        }
    } catch (error) {
        isLoading.value = false;
        handleError(error);
    }
    bookingStore.bookingData = bookingDetails.addTripResponse.booking;
    await router.push({ path: "/your-basket/", query: { bookingId: bookingStore.bookingData.id } });
    isLoading.value = false;
}

function submitPreferences() {
    createBooking();
}

watch(toggleModel, () => {
    hasOptedForReservation.value = toggleModel.value;
});
</script>

<template>
    <div>
        <v-container class="pa-0 pa-md-2 pb-5 bg-white">
            <v-row class="pl-10 pt-5 pb-1 pr-10">
                <v-col cols="12">
                    <div class="head-section">
                        <v-switch v-model="toggleModel" hide-details color="#FF7962" v-if="!seatReservationRequired"></v-switch>
                        <div class="horizontal-fields radio-type default-options pb-5" v-if="!seatReservationRequired">
                            <h2>Would you like to reserve a seat?</h2>
                            <div class="pl-5 radio-option">
                                <v-btn :class="[hasOptedForReservation ? 'mtt-button' : 'mtt-button-inverted']" :disabled="isLoading" @click="hasOptedForReservation = true">Yes</v-btn>
                            </div>
                            <div class="radio-option">
                                <v-btn :class="[!hasOptedForReservation ? 'mtt-button' : 'mtt-button-inverted']" :disabled="isLoading" @click="hasOptedForReservation = false">No</v-btn>
                            </div>
                        </div>
                        <div></div>
                    </div>
                    <div v-if="hasOptedForReservation || seatReservationRequired" class="horizontal-fields reservation default-options pb-5">
                        <h2 class="pb-2">Seat Reservations</h2>
                        <div v-if="seatReservationRequired">Note: Reservations are compulsory on this journey.</div>
                        <v-divider opacity="0"></v-divider>

                        <div class="preferences-section pt-5 pb-5">
                            <div class="row selected-preferences">
                                <div>
                                    <v-select label="Seat Direction" :items="bookingStore.seatDirectionDropdownOptions" v-model="selectedSeatDirection" :readonly="isLoading" variant="outlined"></v-select>
                                </div>
                                <div>
                                    <v-select label="Position" :items="seatPositionsDropdown" v-model="selectedPosition" :readonly="isLoading" variant="outlined"></v-select>
                                </div>
                                <div>
                                    <v-select label="Coach Type" :items="coachType" v-model="selectedCoachType" :readonly="isLoading" variant="outlined"></v-select>
                                </div>
                            </div>

                            <div class="optional-preferences">
                                <v-checkbox :label="ReservationPreferences.TableSeat" @click="toggleTableSeat()" :readonly="isLoading"></v-checkbox>
                                <v-checkbox :label="ReservationPreferences.NearLuggage" @click="toggleLuggageRack()" :readonly="isLoading"></v-checkbox>
                                <v-checkbox :label="ReservationPreferences.PowerSocket" @click="togglePowerSocket()" :readonly="isLoading"></v-checkbox>
                                <v-checkbox :label="ReservationPreferences.NearToilet" @click="toggleNearToilet()" :readonly="isLoading"></v-checkbox>
                            </div>
                            <div class="pb-5">We will request seats with your selected preferences, though they cannot always be guaranteed by the train operator. Seating information will be confirmed upon reaching the shopping basket.</div>
                            <v-divider opacity="0"></v-divider>
                        </div>
                    </div>
                </v-col>
            </v-row>
            <v-btn class="mtt-button ml-5 mb-5" @click="submitPreferences()" :loading="isLoading">Continue</v-btn>
        </v-container>
    </div>
</template>

<style scoped lang="scss">
@import "@/assets/base.scss";

.v-input.v-input--horizontal {
    width: 200px;
    padding-left: 5px;
}

.row.selected-preferences {
    display: inline-flex;
}

:deep(.v-selection-control--dirty .v-selection-control__input > .v-icon) {
    color: var(--vt-c-orange);
}

.optional-preferences {
    display: flex;
    flex-wrap: wrap;
    width: 50%;

    .v-input.v-checkbox {
        flex: 1 1 calc(50%);
    }
}

.v-container {
    border: 3px solid var(--vt-c-orange);
    border-radius: 5px;
    margin-top: 2rem;
}

span.v-chip.v-theme--light.v-chip--density-default.v-chip--size-default.v-chip--variant-tonal {
    background-color: var(--vt-c-orange);
    color: var(--vt-c-white);
}

button.primary-button {
    box-shadow: none;
}

.v-input.v-input--horizontal.v-input--center-affix.v-input--density-default.v-locale--is-ltr.v-switch {
    display: none;
}

.radio-type input[type="radio"]:checked + label {
    background-color: var(--vt-c-orange);
    color: var(--vt-c-white);
}

.radio-type label {
    font-weight: normal;
    display: inline-block;
    background-color: transparent;
    border: 2px solid var(--vt-c-orange);
    border-radius: 5px;
    color: var(--vt-c-orange);
    cursor: pointer;
    height: 35px;
    width: 110px;
    padding: 0.25rem 0.5rem;
    line-height: 1.5;
    border-radius: 20px;
}

.radio-type label {
    text-align: center;
}

.horizontal-fields.reservation {
    display: block;
}

@media only screen and (max-width: $mobile-breakpoint) {
    .v-container {
        min-width: auto;
        border: 2px solid var(--vt-c-widget-box-shadow);
        margin-top: 0;
    }

    .row.selected-preferences {
        display: block;

        .v-input {
            width: 100%;
        }
    }

    span.v-chip {
        float: right;
    }

    .optional-preferences {
        width: 100%;
    }

    .v-row.pl-10.pt-10.pb-10.pr-10 {
        margin: 0;
    }

    .v-input.v-input--horizontal.v-input--center-affix.v-input--density-default.v-locale--is-ltr.v-switch {
        display: block;
        float: right;
        width: 40px;
        margin-top: -10px;
    }

    .radio-option {
        display: none;
    }
}
</style>
